function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import debounce from 'lodash/debounce';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import map from 'lodash/map';
import unionBy from 'lodash/unionBy';
import isObject from 'lodash/isObject';
import isFunction from 'lodash/isFunction';
import { useAction } from 'hooks';
import Alert from 'components/Alert';
import * as actions from '../../actions';
import * as selectors from '../../selectors';
import { transformSystemAlerts } from './helpers';
const onAction = (action, dispatch)=>{
    if (isFunction(action)) {
        action();
    } else if (isObject(action)) {
        dispatch(action);
    }
};
const getAlertName = (alert)=>isObject(alert.message) ? `${alert.message.id}_${JSON.stringify(alert.messageValues)}` : alert.message;
const useAlerts = (isGlobal)=>{
    const newAlerts = useSelector(selectors.alerts);
    const newAlertsTimeThreshold = useSelector(selectors.alertsTimeThreshold);
    const dismissedAlerts = useSelector(selectors.dismissedAlerts);
    const openModalId = useSelector(selectors.modal);
    const route = useSelector(selectors.route);
    const alertsTimeThreshold = useRef(+moment.utc().locale('en').format('X'));
    const prevRouteRef = useRef(null);
    const [alerts, setAlerts] = useState([]);
    const dismissAlert = useAction(actions.dismissAlert);
    const onDisplayAlerts = debounce(useAction(actions.displayAlerts), 150);
    const onCloseAlert = (alert)=>{
        const { id } = alert;
        setAlerts(alerts.filter((a)=>a.id !== id));
        if (alert.attributes && alert.attributes.isPinned) {
            dismissAlert(alert);
        }
    };
    useEffect(()=>{
        let currentAlerts = alerts;
        if (prevRouteRef.current && route !== prevRouteRef.current) {
            currentAlerts = filter(alerts, (alert)=>alert.attributes && alert.attributes.isPinned);
        }
        if (newAlerts.length || newAlertsTimeThreshold > alertsTimeThreshold.current) {
            const localAlerts = filter(newAlerts, (alert)=>{
                const alertName = getAlertName(alert);
                return !includes(dismissedAlerts, alertName) && (!openModalId || openModalId && (isGlobal && alert.isGlobal || !isGlobal && !alert.isGlobal));
            });
            let unionAlerts = unionBy([
                ...currentAlerts
            ].reverse(), localAlerts, (alert)=>getAlertName(alert));
            unionAlerts = filter(unionAlerts, (alert)=>alert.timestamp >= alertsTimeThreshold.current).reverse();
            setAlerts(unionAlerts);
            alertsTimeThreshold.current = newAlertsTimeThreshold;
            prevRouteRef.current = route;
            onDisplayAlerts();
        } else {
            setAlerts(currentAlerts);
        }
    }, [
        newAlerts,
        newAlertsTimeThreshold,
        route
    ]);
    return {
        alerts,
        onCloseAlert
    };
};
const useSystemAlerts = (isGlobal)=>{
    if (!isGlobal) {
        return {
            systemAlerts: [],
            onCloseSystemAlert: undefined
        };
    }
    const newSystemAlerts = useSelector(selectors.systemAlerts);
    const systemAlertsSettings = useSelector(selectors.systemAlertsSettings);
    const localizationResources = useSelector(selectors.localizationResources);
    const dismissSystemAlert = useAction(actions.dismissSystemAlert);
    const [systemAlerts, setSystemAlerts] = useState([]);
    const onDisplayAlerts = debounce(useAction(actions.displayAlerts), 150);
    const onCloseSystemAlert = (alert)=>{
        const { id } = alert;
        setSystemAlerts(systemAlerts.filter((a)=>a.id !== id));
        dismissSystemAlert(alert);
    };
    useEffect(()=>{
        if (newSystemAlerts.length) {
            let unionSystemAlerts = [];
            unionSystemAlerts = unionBy([
                ...systemAlerts
            ].reverse(), newSystemAlerts, (systemAlert)=>`${systemAlert.alertId}_${systemAlert.createTimestamp}`);
            if (unionSystemAlerts.length <= systemAlerts.length) {
                return;
            }
            unionSystemAlerts = transformSystemAlerts(unionSystemAlerts, systemAlertsSettings, localizationResources).filter((systemAlert)=>systemAlert && systemAlert.message).reverse();
            setSystemAlerts(unionSystemAlerts);
            onDisplayAlerts();
        }
    }, [
        newSystemAlerts
    ]);
    return {
        systemAlerts,
        onCloseSystemAlert
    };
};
const AlertsBus = ({ className, isGlobal = false })=>{
    const dispatch = useDispatch();
    const { alerts, onCloseAlert } = useAlerts(isGlobal);
    const { systemAlerts = [], onCloseSystemAlert } = useSystemAlerts(isGlobal);
    const allAlerts = [
        ...alerts,
        ...systemAlerts
    ];
    const [isCollapsed, setIsCollapsed] = useState(false);
    const onClose = (alert)=>{
        const { alertConfigurationId } = alert;
        if (alertConfigurationId) {
            if (onCloseSystemAlert) onCloseSystemAlert(alert);
        } else {
            onCloseAlert(alert);
        }
    };
    const onToggleCollapse = ()=>{
        setIsCollapsed(!isCollapsed);
    };
    return /*#__PURE__*/ _jsx(motion.div, {
        className: cn('alertsBus', className),
        initial: {
            height: 0
        },
        animate: {
            height: allAlerts.length ? 'auto' : 0
        },
        transition: {
            ease: 'easeIn',
            duration: allAlerts.length ? 0.1 : 0.3
        },
        style: {
            overflow: 'hidden'
        },
        children: /*#__PURE__*/ _jsx(AnimatePresence, {
            children: map(allAlerts, (alert, idx)=>/*#__PURE__*/ _jsx(motion.div, {
                    initial: {
                        opacity: 0,
                        marginTop: isCollapsed ? '-55px' : '-55px'
                    },
                    animate: {
                        opacity: 1,
                        marginTop: idx && isCollapsed ? '-52px' : 0
                    },
                    exit: {
                        opacity: 0,
                        marginTop: '-55px'
                    },
                    transition: {
                        ease: 'linear',
                        duration: 0.3
                    },
                    style: {
                        position: 'relative',
                        zIndex: allAlerts.length - idx
                    },
                    children: /*#__PURE__*/ _jsx(Alert, _object_spread_props(_object_spread({
                        idx: idx,
                        isCollapsed: isCollapsed
                    }, alert), {
                        actions: alert.actions && alert.actions.map((action)=>_object_spread_props(_object_spread({}, action), {
                                action: ()=>onAction(action.action, dispatch)
                            })),
                        onClose: ()=>onClose(alert),
                        onToggleCollapse: onToggleCollapse
                    }))
                }, `alert-${isObject(alert.message) ? alert.message.id : alert.message}-${alert.id}`))
        })
    });
};
export default AlertsBus;
